<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanAutomationsAPI from 'dashboard/api/kanbanAutomations';

const props = defineProps({
  automationId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const logs = ref([]);
const isLoading = ref(false);
const hasError = ref(false);
const expanded = ref(false);

const STATUS_CLASSES = {
  executed: 'text-n-teal-11 bg-n-teal-2',
  scheduled: 'text-n-blue-11 bg-n-blue-2',
  skipped_filter: 'text-n-amber-11 bg-n-amber-2',
  skipped_column_changed: 'text-n-amber-11 bg-n-amber-2',
  skipped_already_executed: 'text-n-slate-11 bg-n-alpha-2',
  skipped_max_depth: 'text-n-amber-11 bg-n-amber-2',
  failed: 'text-n-ruby-11 bg-n-ruby-2',
};

function statusLabel(status) {
  return t(`KANBAN.AUTOMATIONS.PANEL.LOGS.STATUS.${status.toUpperCase()}`);
}

function statusClass(status) {
  return STATUS_CLASSES[status] || 'text-n-slate-11 bg-n-alpha-2';
}

function formatDateTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function loadLogs() {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await KanbanAutomationsAPI.getLogs(props.automationId);
    logs.value = data?.data ?? [];
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function toggle() {
  expanded.value = !expanded.value;
  if (expanded.value && !logs.value.length && !hasError.value) {
    loadLogs();
  }
}

onMounted(() => {
  if (expanded.value) loadLogs();
});
</script>

<template>
  <div
    class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-background p-2.5"
  >
    <button
      type="button"
      class="flex items-center justify-between text-sm font-medium text-n-slate-12"
      @click="toggle"
    >
      <span class="flex items-center gap-2">
        <span class="i-lucide-history size-4 text-n-slate-10" />
        {{ t('KANBAN.AUTOMATIONS.PANEL.LOGS.TITLE') }}
      </span>
      <span
        class="size-4 text-n-slate-10"
        :class="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      />
    </button>

    <template v-if="expanded">
      <p v-if="isLoading" class="text-xs text-n-slate-10">
        {{ t('KANBAN.AUTOMATIONS.PANEL.LOGS.LOADING') }}
      </p>
      <p v-else-if="hasError" class="text-xs text-n-ruby-11">
        {{ t('KANBAN.AUTOMATIONS.PANEL.LOGS.ERROR') }}
      </p>
      <p v-else-if="!logs.length" class="text-xs text-n-slate-10 italic">
        {{ t('KANBAN.AUTOMATIONS.PANEL.LOGS.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
        <li
          v-for="log in logs"
          :key="log.id"
          class="flex items-start justify-between gap-2 text-xs"
        >
          <div class="flex flex-col gap-0.5 min-w-0">
            <span
              class="self-start rounded px-1.5 py-0.5 font-medium"
              :class="statusClass(log.status)"
            >
              {{ statusLabel(log.status) }}
            </span>
            <span
              v-if="log.reason"
              class="text-n-slate-10 truncate"
              :title="log.reason"
            >
              {{ log.reason }}
            </span>
          </div>
          <span class="shrink-0 text-n-slate-9">{{
            formatDateTime(log.created_at)
          }}</span>
        </li>
      </ul>
    </template>
  </div>
</template>
